import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { AppLayout } from "@/components/AppLayout";
import {
  Search, Users, Briefcase, MapPin, Mail, X, ChevronRight, ChevronDown,
  Crown, ZoomIn, ZoomOut, Maximize2, UserCircle2, LayoutList, Network,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetEmployees, type EmployeeListItem } from "@/lib/api";
import { getContractTypeLabel } from "@/lib/employment-contract-types";
import { cn } from "@/lib/utils";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { TableSkeleton, CardGridSkeleton } from "@/components/PageSkeleton";

const statusColors: Record<string, string> = {
  Active:   "bg-success/10 text-success border-success/20",
  Disabled: "bg-slate-100 text-slate-500 border-slate-200",
};

const statusKeys: Record<string, string> = {
  Active:   "common.active",
  Disabled: "common.disabled",
};

const MIN_SCALE = 0.25;
const MAX_SCALE = 2;
const ZOOM_STEP = 0.15;

const Employees = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { data: employees = [], isLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: apiGetEmployees,
  });

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"orgchart" | "list">("orgchart");

  // ── Canvas state ──────────────────────────────────────────────────────────
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 40, y: 20 });
  const isDragging = useRef(false);
  const dragOrigin = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const registerCardRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) cardRefs.current.set(id, el);
    else cardRefs.current.delete(id);
  }, []);

  // Center tree horizontally after first render
  useEffect(() => {
    if (!contentRef.current || !containerRef.current) return;
    const timer = setTimeout(() => {
      const cw = containerRef.current!.clientWidth;
      const contentW = contentRef.current!.scrollWidth;
      setTranslate({ x: Math.max(40, (cw - contentW) / 2), y: 20 });
    }, 50);
    return () => clearTimeout(timer);
  }, [isLoading, employees.length]);

  // ── Wheel zoom centered on mouse ──────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const delta = e.deltaY < 0 ? 1 + ZOOM_STEP : 1 - ZOOM_STEP;
      setScale((prev) => {
        const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * delta));
        const ratio = next / prev;
        setTranslate((pt) => ({
          x: mx - (mx - pt.x) * ratio,
          y: my - (my - pt.y) * ratio,
        }));
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // ── Drag / pan ────────────────────────────────────────────────────────────
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    isDragging.current = true;
    dragOrigin.current = { x: e.clientX - translate.x, y: e.clientY - translate.y };
    e.currentTarget.setPointerCapture?.((e.nativeEvent as PointerEvent).pointerId ?? 0);
  }, [translate]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    setTranslate({
      x: e.clientX - dragOrigin.current.x,
      y: e.clientY - dragOrigin.current.y,
    });
  }, []);

  const onMouseUp = useCallback(() => { isDragging.current = false; }, []);

  // ── Zoom buttons ──────────────────────────────────────────────────────────
  const zoomBy = useCallback((factor: number) => {
    const el = containerRef.current;
    if (!el) return;
    const cx = el.clientWidth / 2;
    const cy = el.clientHeight / 2;
    setScale((prev) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * factor));
      const ratio = next / prev;
      setTranslate((pt) => ({
        x: cx - (cx - pt.x) * ratio,
        y: cy - (cy - pt.y) * ratio,
      }));
      return next;
    });
  }, []);

  const resetView = useCallback(() => {
    if (!contentRef.current || !containerRef.current) return;
    const cw = containerRef.current.clientWidth;
    const contentW = contentRef.current.scrollWidth;
    setScale(1);
    setTranslate({ x: Math.max(40, (cw - contentW) / 2), y: 20 });
  }, []);

  // ── Focus on me ───────────────────────────────────────────────────────────
  const focusOnMe = useCallback(() => {
    const empId = user?.employeeId;
    if (!empId || !containerRef.current) return;
    const cardEl = cardRefs.current.get(empId);
    if (!cardEl) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();
    const cardCX = cardRect.left - containerRect.left + cardRect.width / 2;
    const cardCY = cardRect.top - containerRect.top + cardRect.height / 2;
    const containerCX = containerRect.width / 2;
    const containerCY = containerRect.height / 2;
    setTranslate((prev) => ({
      x: prev.x + (containerCX - cardCX),
      y: prev.y + (containerCY - cardCY),
    }));
  }, [user?.employeeId]);

  // ── Data ──────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return employees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q) ||
        e.jobTitle.toLowerCase().includes(q) ||
        e.department.toLowerCase().includes(q)
    );
  }, [employees, search]);

  const getDirectReports = useCallback(
    (id: string) => employees.filter((e) => e.reportsTo?.id === id),
    [employees]
  );

  const { cLevelRoots, otherRoots, benchEmployees } = useMemo(() => {
    const noManager = employees.filter((e) => !e.reportsTo);
    return {
      cLevelRoots: noManager.filter((e) => e.subRole === "c_level"),
      otherRoots: noManager.filter((e) => e.subRole !== "c_level" && e.isLineManager),
      benchEmployees: noManager.filter((e) => e.subRole !== "c_level" && !e.isLineManager),
    };
  }, [employees]);

  const myEmpId = user?.employeeId ?? null;

  return (
    <AppLayout>
      <div className="flex flex-col h-[calc(100vh-4rem)] -m-6 overflow-hidden">
        {/* ── Top bar ── */}
        <div className="flex items-center gap-4 px-6 py-3 border-b bg-background shrink-0 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">{t("employees.title")}</h1>
              <p className="text-xs text-muted-foreground">{employees.length} {t("employees.title").toLowerCase()}</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("employees.searchPlaceholder")}
              className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-8 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/20"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {search.trim() && (
              <div className="absolute top-full left-0 right-0 mt-1 rounded-xl border bg-card shadow-lg max-h-72 overflow-y-auto z-50">
                {!isLoading && filtered.length === 0 && <p className="p-4 text-sm text-muted-foreground text-center">{t("employees.notFound")}</p>}
                {filtered.map((emp) => (
                  <button key={emp.id} onClick={() => navigate(`/employees/${emp.id}`)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left">
                    <EmployeeAvatar avatarUrl={emp.avatarUrl} avatarColor={emp.avatarColor} initials={emp.initials} name={emp.name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{emp.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{emp.jobTitle} · {emp.department}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1 ml-auto">
            {/* View toggle */}
            <div className="flex items-center gap-0.5 border rounded-lg p-0.5 bg-background">
              <button
                onClick={() => setView("orgchart")}
                className={cn("h-7 w-7 flex items-center justify-center rounded transition-colors", view === "orgchart" ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground")}
                title="Org chart view"
              >
                <Network className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setView("list")}
                className={cn("h-7 w-7 flex items-center justify-center rounded transition-colors", view === "list" ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground")}
                title="List view"
              >
                <LayoutList className="h-3.5 w-3.5" />
              </button>
            </div>
            {view === "orgchart" && (
              <>
                {myEmpId && (
                  <Button variant="outline" size="sm" onClick={focusOnMe} className="gap-1.5 text-xs">
                    <UserCircle2 className="h-4 w-4" />
                    Focus on me
                  </Button>
                )}
                <div className="flex items-center gap-1 border rounded-lg p-0.5">
                  <button onClick={() => zoomBy(1 + ZOOM_STEP)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted transition-colors" title="Zoom in">
                    <ZoomIn className="h-4 w-4" />
                  </button>
                  <span className="text-xs w-10 text-center tabular-nums text-muted-foreground">{Math.round(scale * 100)}%</span>
                  <button onClick={() => zoomBy(1 - ZOOM_STEP)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted transition-colors" title="Zoom out">
                    <ZoomOut className="h-4 w-4" />
                  </button>
                </div>
                <button onClick={resetView} className="h-7 w-7 flex items-center justify-center rounded border hover:bg-muted transition-colors" title="Reset view">
                  <Maximize2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Canvas (org chart) ── */}
        {view === "orgchart" && (
          <div
            ref={containerRef}
            className={cn(
              "flex-1 overflow-hidden bg-muted/20 relative",
              isDragging.current ? "cursor-grabbing" : "cursor-grab",
            )}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            {isLoading ? (
              <div className="p-8">
                <CardGridSkeleton count={6} />
              </div>
            ) : (
              <div
                ref={contentRef}
                style={{
                  transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                  transformOrigin: "0 0",
                  willChange: "transform",
                }}
                className="inline-block"
              >
                <div className="flex flex-col items-center gap-0 p-8">
                  {/* C-level row — all C-level roots side by side */}
                  {cLevelRoots.length > 0 && (
                    <div className="flex flex-row items-start gap-8">
                      {cLevelRoots.map((emp) => (
                        <OrgBranch key={emp.id} emp={emp} getDirectReports={getDirectReports} level={0} t={t} variant="clevel" myEmpId={myEmpId} registerCardRef={registerCardRef} />
                      ))}
                    </div>
                  )}

                  {/* Standalone manager row — team leads with no c-level parent, all side by side */}
                  {otherRoots.length > 0 && (
                    <div className={cn("flex flex-row items-start gap-4", cLevelRoots.length > 0 && "mt-10")}>
                      {otherRoots.map((emp, i) => {
                        const isFirst = i === 0;
                        const isLast = i === otherRoots.length - 1;
                        const isOnly = otherRoots.length === 1;
                        return (
                          <div key={emp.id} className="flex flex-col items-center px-4">
                            {!isOnly && (
                              <div className="relative w-full h-0 mb-0">
                                {/* horizontal connector line between siblings */}
                              </div>
                            )}
                            <OrgBranch emp={emp} getDirectReports={getDirectReports} level={0} t={t} variant="manager" myEmpId={myEmpId} registerCardRef={registerCardRef} />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {benchEmployees.length > 0 && (
                    <div className="mt-10 w-full">
                      <BenchSection employees={benchEmployees} t={t} myEmpId={myEmpId} registerCardRef={registerCardRef} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── List view ── */}
        {view === "list" && (
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden p-4">
                <TableSkeleton rows={8} cols={7} />
              </div>
            ) : (
              <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                      <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">Employee</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Job Title</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">Department</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">Location</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">Contract</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Status</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden xl:table-cell">Start Date</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(search.trim() ? filtered : employees).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground">No employees found.</TableCell>
                      </TableRow>
                    ) : (
                      (search.trim() ? filtered : employees).map((emp) => (
                        <TableRow
                          key={emp.id}
                          className="border-b border-border/40 hover:bg-muted/40 transition-colors cursor-pointer"
                          onClick={() => navigate(`/employees/${emp.id}`)}
                        >
                          <TableCell className="pl-4 py-3">
                            <div className="flex items-center gap-3">
                              <EmployeeAvatar avatarUrl={emp.avatarUrl} avatarColor={emp.avatarColor} initials={emp.initials} name={emp.name} size="sm" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{emp.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{emp.employeeId}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="text-sm text-foreground">{emp.jobTitle || "—"}</span>
                          </TableCell>
                          <TableCell className="py-3 hidden md:table-cell">
                            <span className="text-sm text-muted-foreground">{emp.department || "—"}</span>
                          </TableCell>
                          <TableCell className="py-3 hidden lg:table-cell">
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5 shrink-0" />
                              {emp.location || "—"}
                            </div>
                          </TableCell>
                          <TableCell className="py-3 hidden lg:table-cell">
                            <span className="text-sm text-muted-foreground">{getContractTypeLabel(emp.contractType, t) || "—"}</span>
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge variant="outline" className={`text-[10px] border ${statusColors[emp.status] ?? statusColors.Active}`}>
                              {t(statusKeys[emp.status] ?? "common.active")}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3 hidden xl:table-cell">
                            <span className="text-sm text-muted-foreground">
                              {emp.startDate ? new Date(emp.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 w-10">
                            <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

// ── OrgBranch ────────────────────────────────────────────────────────────────

interface OrgBranchProps {
  emp: EmployeeListItem;
  getDirectReports: (id: string) => EmployeeListItem[];
  level: number;
  t: TFunction;
  variant?: "clevel" | "manager" | "employee";
  myEmpId: string | null;
  registerCardRef: (id: string, el: HTMLDivElement | null) => void;
}

function OrgBranch({ emp, getDirectReports, level, t, variant = "employee", myEmpId, registerCardRef }: OrgBranchProps) {
  const navigate = useNavigate();
  const reports = getDirectReports(emp.id);
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="flex flex-col items-center">
      <EmpCard
        emp={emp}
        variant={variant}
        hasReports={reports.length > 0}
        expanded={expanded}
        isMe={myEmpId === emp.id}
        onToggle={reports.length > 0 ? (e) => { e.stopPropagation(); setExpanded((v) => !v); } : undefined}
        onNavigate={(e) => { e.stopPropagation(); navigate(`/employees/${emp.id}`); }}
        t={t}
        registerRef={(el) => registerCardRef(emp.id, el)}
      />

      {reports.length > 0 && expanded && (
        <>
          <div className="w-px h-8 bg-border shrink-0" />
          <div className="flex flex-row items-start">
            {reports.map((r, i) => {
              const isFirst = i === 0;
              const isLast = i === reports.length - 1;
              const isOnly = reports.length === 1;
              return (
                <div key={r.id} className="flex flex-col items-center px-4">
                  <div className="relative h-8 w-full">
                    {!isOnly && (
                      <div className={cn(
                        "absolute top-0 h-px bg-border",
                        isFirst ? "left-1/2 right-0" : isLast ? "left-0 right-1/2" : "left-0 right-0"
                      )} />
                    )}
                    <div className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-border" />
                  </div>
                  <OrgBranch
                    emp={r}
                    getDirectReports={getDirectReports}
                    level={level + 1}
                    t={t}
                    variant={r.isLineManager ? "manager" : "employee"}
                    myEmpId={myEmpId}
                    registerCardRef={registerCardRef}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── EmpCard ──────────────────────────────────────────────────────────────────

interface EmpCardProps {
  emp: EmployeeListItem;
  variant: "clevel" | "manager" | "employee";
  hasReports: boolean;
  expanded: boolean;
  isMe: boolean;
  onToggle?: (e: React.MouseEvent) => void;
  onNavigate: (e: React.MouseEvent) => void;
  t: TFunction;
  registerRef: (el: HTMLDivElement | null) => void;
}

function EmpCard({ emp, variant, hasReports, expanded, isMe, onToggle, onNavigate, t, registerRef }: EmpCardProps) {
  return (
    <div
      ref={registerRef}
      className={cn(
        "relative rounded-xl border transition-all group select-none",
        variant === "clevel"   && "w-64 border-violet-300 dark:border-violet-700 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/30 dark:to-indigo-950/30 shadow-md",
        variant === "manager"  && "w-56 border-primary/30 bg-card shadow-sm",
        variant === "employee" && "w-48 bg-card border-border shadow-sm",
        isMe && "ring-2 ring-primary ring-offset-2",
      )}
    >
      <div
        className="p-3 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 rounded-xl transition-colors"
        onClick={onNavigate}
      >
        <div className="flex items-center gap-2.5">
          <EmployeeAvatar avatarUrl={emp.avatarUrl} avatarColor={emp.avatarColor} initials={emp.initials} name={emp.name} size="md" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <p className={cn(
                "text-sm font-semibold truncate",
                variant === "clevel" && "text-violet-900 dark:text-violet-100",
              )}>{emp.name}</p>
              {variant === "clevel" && <Crown className="h-3.5 w-3.5 text-violet-500 shrink-0" />}
              {isMe && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-primary text-primary-foreground leading-none shrink-0">YOU</span>}
            </div>
            <p className="text-xs text-muted-foreground truncate">{emp.jobTitle}</p>
            <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${statusColors[emp.status] ?? statusColors.Active}`}>
              {t(statusKeys[emp.status] ?? "common.active")}
            </span>
          </div>
        </div>

        <div className="mt-2 space-y-0.5">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Briefcase className="h-3 w-3 shrink-0" />
            <span className="truncate">{emp.department}</span>
          </div>
          {emp.location && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{emp.location}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/50">
          <Link
            to={`/employees/${emp.id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-primary hover:underline"
          >
            {t("employees.viewProfile")}
          </Link>
          <span className="text-border">|</span>
          <a
            href={`mailto:${emp.email}`}
            onClick={(e) => e.stopPropagation()}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Mail className="h-3 w-3" />
          </a>
          {hasReports && (
            <>
              <span className="text-border ml-auto">|</span>
              <span className="text-xs text-muted-foreground">{emp.directReportsCount}</span>
            </>
          )}
        </div>
      </div>

      {/* Expand/collapse toggle */}
      {hasReports && onToggle && (
        <button
          onClick={onToggle}
          className="absolute -bottom-3.5 left-1/2 -translate-x-1/2 z-10 h-7 w-7 rounded-full border bg-card shadow-sm flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      )}
    </div>
  );
}

// ── BenchSection ─────────────────────────────────────────────────────────────

function BenchSection({ employees, t, myEmpId, registerCardRef }: {
  employees: EmployeeListItem[];
  t: TFunction;
  myEmpId: string | null;
  registerCardRef: (id: string, el: HTMLDivElement | null) => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs font-medium px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 shrink-0">
          {t("employees.onBench", "On Bench")} · {employees.length}
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <div className="flex flex-wrap gap-3">
        {employees.map((emp) => (
          <div
            key={emp.id}
            ref={(el) => registerCardRef(emp.id, el)}
            onClick={() => navigate(`/employees/${emp.id}`)}
            className={cn(
              "flex items-center gap-2.5 px-3 py-2.5 rounded-xl border bg-amber-50/50 dark:bg-amber-950/20 cursor-pointer hover:bg-amber-100/60 dark:hover:bg-amber-950/40 transition-colors w-52",
              myEmpId === emp.id
                ? "border-primary ring-2 ring-primary ring-offset-2"
                : "border-amber-200 dark:border-amber-800",
            )}
          >
            <EmployeeAvatar avatarUrl={emp.avatarUrl} avatarColor={emp.avatarColor} initials={emp.initials} name={emp.name} size="sm" />
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium truncate text-amber-900 dark:text-amber-100">{emp.name}</p>
                {myEmpId === emp.id && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-primary text-primary-foreground leading-none shrink-0">YOU</span>}
              </div>
              <p className="text-xs text-amber-700/70 dark:text-amber-400/70 truncate">{emp.jobTitle}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Employees;
